import React from 'react';
import { Dismiss20Regular } from '@fluentui/react-icons';
import styles from '../css/vdr.module.css';
import { StatusDot } from '../components/StatusUI';
import { VDRFileTypeIcon } from '../components/VDRItemIcons';

const MetaStack = ({ label, value }) => (
    <div className={styles.metaStack}>
        <div className={styles.metaStackLabel}>{label}</div>
        <div className={styles.metaStackValue}>{value ?? '—'}</div>
    </div>
);

function mailPartIndexStatus(mail, indexedDocumentId) {
    if (mail.status === 'processing') {
        return { status: 'processing', label: 'Indexing…' };
    }
    if (indexedDocumentId) {
        return { status: 'ready', label: 'Indexed' };
    }
    if (mail.status === 'needs_attention') {
        return { status: 'needs_attention', label: 'Not indexed' };
    }
    return { status: 'needs_attention', label: 'Not indexed' };
}

const MailDetailPanel = ({ mail, onClose, onCopyLink, onRetry, onOpenAttachment, onDownloadAttachment, onOpenRelated }) => {
    const [retrying, setRetrying] = React.useState(false);
    if (!mail) return null;

    const isProcessing = mail.status === 'processing';
    const needsAttention = mail.status === 'needs_attention';
    const isReady = !isProcessing && !needsAttention;

    const formatCc = cc => {
        if (!cc?.length) return null;
        return cc
            .map(c => (typeof c === 'string' ? c : `${c.name || ''}${c.email ? ` <${c.email}>` : ''}`))
            .join(', ');
    };

    const handleRetry = async () => {
        if (!onRetry) return;
        setRetrying(true);
        try {
            await onRetry(mail);
        } finally {
            setRetrying(false);
        }
    };

    const attachments = mail.attachments || [];
    const indexedAttachmentCount = attachments.filter(att => att.indexedDocumentId).length;
    const bodyIndex = mailPartIndexStatus(mail, mail.indexedDocumentId);

    return (
        <aside className={styles.panel}>
            <div className={styles.panelHeader}>
                <div className={styles.panelHeaderMain}>
                    <h3 className={styles.panelTitle} style={{ fontSize: 15 }}>
                        {mail.subject}
                    </h3>
                    <div className={styles.panelHeaderBadges}>
                        <StatusDot status={mail.status} />
                    </div>
                </div>
                <div className={styles.panelHeaderActions}>
                    <button type="button" className={styles.copyLinkBtn} onClick={() => onCopyLink?.(mail)}>
                        Copy link
                    </button>
                    <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close">
                        <Dismiss20Regular />
                    </button>
                </div>
            </div>

            <div className={styles.panelBody}>
                <MetaStack
                    label="From"
                    value={
                        <>
                            {mail.senderName} &lt;{mail.senderEmail}&gt;
                        </>
                    }
                />
                <MetaStack label="To" value={mail.to} />
                {isReady && mail.cc?.length > 0 ? <MetaStack label="Cc" value={formatCc(mail.cc)} /> : null}
                <MetaStack label="Received" value={mail.received} />

                {isProcessing ? (
                    <div className={styles.sectionBlock}>
                        <div className={styles.mailProcessingBanner}>
                            Lens is cleaning and indexing this email. Content may be available before processing completes.
                        </div>
                    </div>
                ) : null}

                {needsAttention ? (
                    <div className={styles.sectionBlock}>
                        <h4 className={styles.sectionHeading}>Issue</h4>
                        <p className={styles.issueMessage}>{mail.issueMessage || 'Lens could not fully process this source.'}</p>
                        <div className={styles.issueActions}>
                            <button
                                type="button"
                                className={styles.secondaryBtn}
                                style={{ width: '100%', justifyContent: 'center' }}
                                onClick={handleRetry}
                                disabled={retrying}
                            >
                                {retrying ? 'Retrying…' : 'Retry processing'}
                            </button>
                        </div>
                    </div>
                ) : null}

                {isReady && mail.body ? <div className={styles.mailBodyCard}>{mail.body}</div> : null}

                {attachments.length > 0 ? (
                    <div className={styles.sectionBlock}>
                        <p className={styles.sectionHeading}>Attachments</p>
                        {!isProcessing ? (
                            <p className={styles.attachmentIndexSummary}>
                                {indexedAttachmentCount} of {attachments.length} indexed in Lens
                            </p>
                        ) : null}
                        {attachments.map(att => {
                            const indexStatus = mailPartIndexStatus(mail, att.indexedDocumentId);
                            return (
                                <div key={att.id} className={styles.attachmentRowRich}>
                                    <VDRFileTypeIcon extension={att.extension} />
                                    <div className={styles.attachmentMeta}>
                                        <div className={styles.attachmentName}>{att.name}</div>
                                        <div className={styles.attachmentIndexRow}>
                                            <StatusDot status={indexStatus.status} label={indexStatus.label} />
                                        </div>
                                        {att.sizeLabel ? <div className={styles.attachmentSize}>{att.sizeLabel}</div> : null}
                                    </div>
                                    <div className={styles.attachmentActions}>
                                        <button type="button" className={styles.textLink} onClick={() => onOpenAttachment?.(att)}>
                                            Open
                                        </button>
                                        <button type="button" className={styles.attachmentDownload} onClick={() => onDownloadAttachment?.(att)}>
                                            Download
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : null}

                {isReady ? (
                    <div className={styles.sectionBlock}>
                        <p className={styles.sectionHeading}>Related</p>
                        <button
                            type="button"
                            className={styles.relatedRow}
                            style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left' }}
                            onClick={() => onOpenRelated?.('related_irl', mail)}
                        >
                            <span>Information Requests</span>
                            <span className={styles.relatedCount}>{mail.relatedIrl || 0} →</span>
                        </button>
                        <button
                            type="button"
                            className={styles.relatedRow}
                            style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left' }}
                            onClick={() => onOpenRelated?.('related_obs', mail)}
                        >
                            <span>Observations</span>
                            <span className={styles.relatedCount}>{mail.relatedObs || 0} →</span>
                        </button>
                    </div>
                ) : null}

                <div className={styles.sectionBlock}>
                    <p className={styles.sectionHeading}>Source</p>
                    <MetaStack label="Lens status" value={<StatusDot status={mail.status} />} />
                    <MetaStack
                        label="Message body"
                        value={<StatusDot status={bodyIndex.status} label={bodyIndex.label} />}
                    />
                    {attachments.length > 0 ? (
                        <MetaStack
                            label="Attachments"
                            value={
                                isProcessing
                                    ? 'Indexing…'
                                    : `${indexedAttachmentCount} of ${attachments.length} indexed`
                            }
                        />
                    ) : null}
                    {isReady ? <MetaStack label="Indexed" value={mail.indexedAt || '—'} /> : null}
                </div>
            </div>
        </aside>
    );
};

export default MailDetailPanel;
